export interface ChunkRecoveryGateInput {
  isOnline: boolean
  inCooldown: boolean
  probeOnline: boolean
}

export type ChunkRecoveryDecision =
  | { action: 'recover' }
  | { action: 'skip'; reason: 'cooldown' | 'offline' | 'unreachable' }

/**
 * Decide whether a chunk-load failure should trigger cache-clearing recovery.
 *
 * A chunk-load error has two possible causes with identical error messages:
 *   1. A new deploy removed the stale chunk hash the client was requesting.
 *   2. The client is offline and the chunk was not precached.
 *
 * Recovery (clearing every Cache Storage bucket + reload) is correct for (1)
 * and catastrophic for (2) — it wipes the precached app shell and offline
 * assets, leaving the user with a white screen until connectivity returns.
 *
 * We therefore require *both* the browser's `navigator.onLine` flag and a
 * real server probe to report connectivity before clearing caches. The probe
 * guard exists because WKWebView and some desktop browsers can report
 * `navigator.onLine === true` behind captive portals or on dead networks.
 */
export function decideChunkRecoveryAction(
  input: ChunkRecoveryGateInput,
): ChunkRecoveryDecision {
  if (input.inCooldown) return { action: 'skip', reason: 'cooldown' }
  if (!input.isOnline) return { action: 'skip', reason: 'offline' }
  if (!input.probeOnline) return { action: 'skip', reason: 'unreachable' }
  return { action: 'recover' }
}
